<script setup>
import PlusIcon from '@iconify-vue/ph/plus';
import { Link } from '@inertiajs/vue3';
import { getLocalTimeZone, startOfMonth, today } from '@internationalized/date';
import MonthCalendar from '@js/components/calendars/MonthCalendar.vue';
import PillBadge from '@js/components/pills/PillBadge.vue';
import { useRoute } from '@js/utils/route';
import { clearToasts, toastError, toastUpdated } from '@js/utils/toast';
import { onMounted, ref, watch } from 'vue';

const { tenant, defaultPublicationTime } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    defaultPublicationTime: {
        type: String,
        required: true,
    },
});

const { route } = useRoute();

const statusVariants = {
    draft: 'neutral',
    scheduled: 'info',
    publishing: 'info',
    published: 'success',
    failed: 'danger',
    canceled: 'neutral',
};

const loading = ref(true);

const posts = ref([]);

const month = ref(startOfMonth(today(getLocalTimeZone())));

function isEditable(post) {
    return post.isEditable;
}

function formatTime(post) {
    return post.scheduledAt.slice(11, 16);
}

function getPostHref(post) {
    return route('front.projectManagement.smo.post', {
        tenant: tenant.id,
        smoPost: post.id,
    });
}

function getCreationHref(day) {
    return route('front.projectManagement.smo.post', {
        tenant: tenant.id,
        scheduledAt: `${day.toString()} ${defaultPublicationTime}`,
    });
}

function loadPosts() {
    loading.value = true;

    axios
        .post(
            route('front.projectManagement.smo.postCalendarPosts', {
                tenant: tenant.id,
            }),
            {
                year: month.value.year,
                month: month.value.month,
            },
        )
        .then((response) => {
            posts.value = response.data;
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

function reschedulePost(post, day) {
    loading.value = true;

    axios
        .post(
            route('front.projectManagement.smo.postReschedulePosts', {
                tenant: tenant.id,
            }),
            {
                ids: [post.id],
                scheduledAt: `${day.toString()} ${post.scheduledAt.slice(11)}`,
            },
        )
        .then(() => {
            clearToasts();
            toastUpdated();
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            loadPosts();
        });
}

watch(month, () => loadPosts());

onMounted(() => loadPosts());
</script>

<template>
    <MonthCalendar
        v-model:month="month"
        :items="posts"
        date-key="scheduledAt"
        :can-move="isEditable"
        :loading="loading"
        @move="reschedulePost"
    >
        <template #actions>
            <slot name="actions" />
        </template>

        <template #day="{ day }">
            <Link
                :href="getCreationHref(day)"
                title="Ajouter une publication ce jour-là"
                class="rounded-sm p-0.5 text-gray-400 opacity-0 duration-150 group-hover:opacity-100 hover:bg-gray-100 hover:text-gray-700"
            >
                <PlusIcon class="size-4" />
            </Link>
        </template>

        <template #item="{ item: post, dragProps, isDragging }">
            <Link
                :href="getPostHref(post)"
                v-bind="dragProps"
                class="flex flex-col gap-1 rounded-sm border border-gray-200 bg-white p-1 text-xs duration-150 hover:border-indigo-400 hover:shadow-sm"
                :class="{
                    'cursor-grab': post.isEditable,
                    'opacity-40': isDragging,
                }"
            >
                <div class="flex items-center gap-1">
                    <span class="font-semibold text-gray-700">
                        {{ formatTime(post) }}
                    </span>

                    <PillBadge
                        :variant="statusVariants[post.status.value]"
                        class="!px-1.5 !py-0 !text-[10px]"
                    >
                        {{ post.status.label }}
                    </PillBadge>
                </div>

                <div class="flex items-start gap-1">
                    <img
                        v-if="post.medias.length"
                        :src="post.medias[0].url"
                        alt=""
                        class="size-8 shrink-0 rounded-xs object-cover"
                    />

                    <span class="line-clamp-2 text-gray-600">
                        {{ post.content }}
                    </span>
                </div>
            </Link>
        </template>
    </MonthCalendar>
</template>
